'use client';
import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import styles from './ResponseTimeChart.module.css';
import type { RequestProductsStatsResponseTime } from '@practium/features/products/types';

interface ResponseTimeChartProps {
	data: RequestProductsStatsResponseTime | undefined;
}

export const ResponseTimeChart = ({ data }: ResponseTimeChartProps) => {
	const [primaryColor, setPrimaryColor] = useState('#453af1');
	const [tickColor, setTickColor] = useState('#767676');
	const [tickFont, setTickFont] = useState('Inter');
	const averageMinutes = Math.round(data?.averageMinutes ?? 0);
	const hours = Math.floor(averageMinutes / 60);
	const minutes = averageMinutes % 60;

	useEffect(() => {
		const style = getComputedStyle(document.documentElement);
		const primary = style.getPropertyValue('--primary').trim();
		const secondary = style.getPropertyValue('--text-secondary').trim();
		const font = style.getPropertyValue('--font-inter').trim();
		if (primary) setPrimaryColor(primary);
		if (secondary) setTickColor(secondary);
		if (font) setTickFont(font);
	}, []);

	return (
		<div className={styles.card}>
			<div className={styles.header}>
				<span className={styles.title}>Tempo Médio de Resposta</span>
			</div>

			<span className={styles.value}>
				{data ? (averageMinutes < 60 ? `${averageMinutes}min` : `${hours}h${minutes}min`) : '—'}
			</span>

			<div className={styles.chartWrapper}>
				<ResponsiveContainer width="95%" height="100%">
					<BarChart data={data?.days ?? []} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
						<YAxis hide />
						<XAxis
							dataKey="date"
							tickFormatter={(date) => data?.days.find((day) => day.date === date)?.label ?? date}
							axisLine={false}
							tickLine={false}
							tick={{
								fontFamily: tickFont,
								fontWeight: 700,
								fontSize: '0.671875rem',
								fill: tickColor,
							}}
						/>
						<Tooltip
							cursor={{ fill: 'rgba(128, 128, 128, 0.08)' }}
							labelFormatter={(date) =>
								new Intl.DateTimeFormat('pt-BR', {
									weekday: 'long',
									timeZone: 'UTC',
								}).format(new Date(`${date}T00:00:00Z`))
							}
							formatter={(value) => [
								Number(value) === 0 ? '<1 min' : `${Number(value).toLocaleString('pt-BR')} min`,
								'Tempo médio',
							]}
							contentStyle={{
								backgroundColor: 'var(--bg-surface)',
								borderColor: 'var(--border)',
								borderRadius: '0.5rem',
								color: 'var(--text-primary)',
							}}
							itemStyle={{ color: 'var(--text-primary)' }}
						/>
						<Bar
							dataKey="averageMinutes"
							minPointSize={(value) => ((value ?? 0) > 0 ? 6 : 0)}
							radius={[4, 4, 0, 0]}
							maxBarSize={25}
							fill={primaryColor}
						/>
					</BarChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
};
